import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProjectService } from '../../../services/project.service';
import { LoadingService } from '../../../services/loading.service';
import { ProjectBudgetService } from '../../../services/project-budget.service';
import { ToastService } from '../../../services/toast.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { ProjectDeletionConfirmComponent } from '../project-deletion-confirm/project-deletion-confirm.component';
import { forkJoin } from 'rxjs';

export type ChartOptions = {
  series: any; // Remove undefined option
  chart: any;
  responsive: any;
  labels: any;
};

@Component({
  selector: 'app-view-project',
  templateUrl: './view-project.component.html',
  styleUrl: './view-project.component.scss'
})
export class ViewProjectComponent implements OnInit {

  // Exponer Math para el template
  Math = Math;

  public showModalNuevoPresupuesto : boolean = false;
  public showModalEditarPresupuesto : boolean = false;
  public showModalEditarProyecto : boolean = false;

  // 🆕 Estado del modal de confirmación de eliminación de rubro
  public showModalEliminarPresupuesto : boolean = false;
  public budgetToDelete : any = null;
  public deletingBudget : boolean = false;

  projectBudgetId! : any;
  projectId! : any;

  public projectData : any;
  public participantsData: any;
  public detailedParticipantsData: any;
  public individualEnrollments: any = [];
  public corporateEnrollments: any = [];
  public corporateParticipants: any = [];
  private _workOrdersByProjectData: any = [];
  public incomeReceiptsData: any = [];
  public excessPaymentsData: any = [];
  public refundVouchersData: any = [];
  totalExecutedAmount: number = 0;
  totalIncomeFromReceipts: number = 0;
  totalExcessPayments: number = 0;
  totalRefundVouchers: number = 0;


public chartOptions!: Partial<ChartOptions>;

// 🆕 Borrado en cascada de proyecto
public canDelete: boolean = false;
@ViewChild('deleteModal') deleteModal!: ProjectDeletionConfirmComponent;

constructor(
  private route: ActivatedRoute,
  private _projectService : ProjectService,
  private _loadingService : LoadingService,
  private _projectBudgetService : ProjectBudgetService,
  private _toastService : ToastService,
  private _authzService : AuthorizationService
){}


  ngOnInit(): void {
    this.canDelete = this._authzService.hasRole('ProjectDeleter');
    this.route.paramMap.subscribe(params => {
      const id: number = Number(params.get('id'));
        this.loadData(id);
        this.projectId = id;

    });
  }

  openDelete(): void {
    this.deleteModal.open();
  }

  loadData(id:any) {
    this._loadingService.show();
    forkJoin({
      participant: this._projectService.getParticipantsByProjectId(id),
      detailedParticipants: this._projectService.getDetailedParticipantsByProjectId(id),
      project: this._projectService.getById(id),
      workOrders:  this._projectService.getWorkOrdersByProjectId(id),
      incomeReceipts: this._projectService.getIncomeReceiptsByProjectId(id),
      excessPayments: this._projectService.getExcessPaymentsByProjectId(id),
      refundVouchers: this._projectService.getRefundVouchersByProjectId(id),
    }).subscribe({
      next: (results) => {
        this.participantsData = results.participant.data;
        this.detailedParticipantsData = results.detailedParticipants.data;
        this.individualEnrollments = this.detailedParticipantsData?.individualEnrollments || [];
        this.corporateEnrollments = this.detailedParticipantsData?.corporateEnrollments || [];
        this.corporateParticipants = this.detailedParticipantsData?.corporateParticipants || [];
        this.projectData = results.project.data;
        this.workOrdersByProjectData = results.workOrders.data;
        this.incomeReceiptsData = results.incomeReceipts.data || [];
        this.excessPaymentsData = results.excessPayments.data || [];
        this.refundVouchersData = results.refundVouchers.data || [];
        this.calculateTotalIncomeFromReceipts();
        this.calculateTotalExcessPayments();
        this.calculateTotalRefundVouchers();
        this._loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading data', error);
        this._loadingService.hide();
      }
    });
  }

  // Setter para disparar la acción cuando se actualiza la variable
  set workOrdersByProjectData(value: any) {
    this._workOrdersByProjectData = value;
    this.calculateTotalExecutedAmount(); // Calcula el total cada vez que se actualice workOrdersByProjectData
  }

  get workOrdersByProjectData(): any {
    return this._workOrdersByProjectData;
  }

  getStatusProject(status: string): string {
    switch (status) {
      case "CREATED":
        return 'Creado';
      case "CLOSED":
        return 'Cerrado';
      case "PRECLOSED":
        return 'Pre-Cierre';
      case "INACTIVE":
        return 'Inactivo';
      case "CANCELED":
        return 'Cancelado';
      default:
        return 'Desconocido';
    }
  }

  getBudgetTypeName(budgetType: string): string {
    switch (budgetType) {
      case "INCOME":
        return 'Ingresos';
      case "EXPENSE":
        return 'Gastos';
      default:
        return 'Desconocido';
    }
  }

  getProjectById(id:number){
    this._projectService.getById(id).subscribe({
      next : (response)=> {
        this.projectData = response.data;
      }
    })
  }

  getWorkOrdersByProjectId(id:number){
    this._projectService.getWorkOrdersByProjectId(id).subscribe({
      next : (response)=> {
        this.workOrdersByProjectData = response.data;
      }
    })
  }

  calculateTotalExecutedAmount(): void {
    this.totalExecutedAmount = this.workOrdersByProjectData.reduce((total:any, order:any) => {
      return total + order.executedAmount;
    }, 0);
  }

  calculateTotalIncomeFromReceipts(): void {
    // ✅ Los montos ya vienen convertidos a COP desde el backend con TRM
    this.totalIncomeFromReceipts = this.incomeReceiptsData.reduce((total:any, receipt:any) => {
      return total + receipt.netAmount;
    }, 0);
  }

  calculateTotalExcessPayments(): void {
    this.totalExcessPayments = this.excessPaymentsData.reduce((total:any, excess:any) => {
      return total + excess.excessAmount;
    }, 0);
  }

  calculateTotalRefundVouchers(): void {
    this.totalRefundVouchers = this.refundVouchersData.reduce((total:any, refund:any) => {
      return total + refund.refundAmount;
    }, 0);
  }

  handleNuevoPresupuesto(){
    this.showModalNuevoPresupuesto = true;
  }

  handleEditarPresupuesto(id:any){
    this.showModalEditarPresupuesto = true;
    this.projectBudgetId = id;
  }

  handleEditarProyecto(id:any){
    this.showModalEditarProyecto = true;
    this.projectId = id;
  }

  closeModalEditarPresupuesto(){
    this.showModalEditarPresupuesto = false;
    this.projectBudgetId = null;
  }

  // 🆕 Confirmación y eliminación de rubro de presupuesto
  handleEliminarPresupuesto(budget: any){
    this.budgetToDelete = budget;
    this.showModalEliminarPresupuesto = true;
  }

  closeModalEliminarPresupuesto(){
    if (this.deletingBudget) return; // No cerrar mientras se está procesando
    this.showModalEliminarPresupuesto = false;
    this.budgetToDelete = null;
  }

  confirmEliminarPresupuesto(){
    if (!this.budgetToDelete?.id || this.deletingBudget) return;

    this.deletingBudget = true;
    this._loadingService.show();

    this._projectBudgetService.delete(this.budgetToDelete.id).subscribe({
      next: (response) => {
        this._loadingService.hide();
        this.deletingBudget = false;

        if (response?.isSuccess) {
          this._toastService.showSuccess('Rubro eliminado correctamente.');
          this.showModalEliminarPresupuesto = false;
          this.budgetToDelete = null;
          this.loadData(this.projectId);
        } else {
          this._toastService.showError(response?.errorMessage || 'No se pudo eliminar el rubro.');
        }
      },
      error: (error) => {
        this._loadingService.hide();
        this.deletingBudget = false;
        // Backend devuelve 400 con texto cuando hay relaciones
        const message = typeof error?.error === 'string'
          ? error.error
          : (error?.error?.errorMessage || 'No se pudo eliminar el rubro.');
        this._toastService.showError(message);
      }
    });
  }

  closeModalEditarProyecto(){
    this.showModalEditarProyecto = false;
  }

  calculatePercentage(actual: number, projected: number): string {
    if (!projected || projected === 0) return '0.00';
    return ((actual / projected) * 100).toFixed(2);
  }

  getIncomeProgressColor(actual: number, projected: number): string {
    const percentage = this.getPercentageValue(actual, projected);
    if (percentage >= 80) return 'text-green-600 dark:text-green-400';
    if (percentage >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  }

  getExpenseProgressColor(actual: number, projected: number): string {
    const percentage = this.getPercentageValue(actual, projected);
    if (percentage <= 80) return 'text-green-600 dark:text-green-400';
    if (percentage <= 100) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  }

  getPercentageValue(actual: number, projected: number): number {
    if (!projected || projected === 0) return 0;
    return (actual / projected) * 100;
  }

  getProgressIcon(actual: number, projected: number, isIncome: boolean): string {
    const percentage = this.getPercentageValue(actual, projected);
    
    if (isIncome) {
      // Para ingresos: más del 50% es bueno (flecha arriba), menos del 50% necesita atención (flecha abajo)
      return percentage >= 50 ? 'up' : 'down';
    } else {
      // Para gastos: menos del 80% es bueno (flecha arriba), más del 100% es malo (flecha abajo)
      if (percentage <= 80) return 'up';
      if (percentage <= 100) return 'neutral';
      return 'down';
    }
  }

  getFeeTypeName(fee: any): string {
    // Si el fee detail tiene un feeType con nombre, lo usamos
    if (fee?.feeType?.name) {
      return fee.feeType.name;
    }
    // Si no, devolvemos un nombre genérico
    return fee?.description || 'Descuento';
  }

  getExcessPaymentStatusName(status: string): string {
    switch (status) {
      case "Available":
        return 'Disponible';
      case "PartiallyUsed":
        return 'Parcialmente Usado';
      case "FullyUsed":
        return 'Completamente Usado';
      case "Expired":
        return 'Expirado';
      default:
        return 'Desconocido';
    }
  }

  getRefundTypeName(type: string): string {
    switch (type) {
      case "ServiceRefund":
        return 'Devolución de Servicio';
      case "ExcessRefund":
        return 'Devolución de Exceso';
      case "CancellationRefund":
        return 'Devolución por Cancelación';
      case "PartialRefund":
        return 'Devolución Parcial';
      default:
        return 'Otro';
    }
  }

  getRefundStatusName(status: string): string {
    switch (status) {
      case "Pending":
        return 'Pendiente';
      case "Approved":
        return 'Aprobado';
      case "Processed":
        return 'Procesado';
      case "Rejected":
        return 'Rechazado';
      case "Cancelled":
        return 'Cancelado';
      case "BankReconciled":
        return 'Conciliado';
      default:
        return 'Desconocido';
    }
  }

  // Métodos para navegación suave a secciones
  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  // Tooltips informativos
  getIncomeTooltip(): string {
    const percentage = this.getPercentageValue(this.totalIncomeFromReceipts, this.projectData?.projectedRevenue);
    return `Ingresos: ${this.totalIncomeFromReceipts | 0} de ${this.projectData?.projectedRevenue | 0} proyectados (${percentage.toFixed(1)}% completado)`;
  }

  getExpenseTooltip(): string {
    const percentage = this.getPercentageValue(this.totalExecutedAmount, this.projectData?.projectedSpend);
    const remaining = (this.projectData?.projectedSpend || 0) - this.totalExecutedAmount;
    return `Gastos: ${this.totalExecutedAmount | 0} ejecutados de ${this.projectData?.projectedSpend | 0} presupuestados. Disponible: ${remaining}`;
  }

  getExcessTooltip(): string {
    return `${this.excessPaymentsData.length} excesos registrados por un total de ${this.totalExcessPayments | 0}`;
  }

  getRefundTooltip(): string {
    return `${this.refundVouchersData.length} devoluciones procesadas por un total de ${this.totalRefundVouchers | 0}`;
  }

  // Métodos para participantes
  getTotalParticipants(): number {
    return this.individualEnrollments.length + this.corporateParticipants.length;
  }

  getTotalCorporateEnrollments(): number {
    return this.corporateEnrollments.length;
  }

  // Función para obtener participantes corporativos de una empresa específica
  getCorporateParticipantsByEnrollment(corporateEnrollmentId: number): any[] {
    return this.corporateParticipants.filter((participant: any) => 
      participant.corporateEnrollmentId === corporateEnrollmentId
    );
  }

  // Función para contar participantes corporativos de una empresa específica
  countCorporateParticipantsByEnrollment(corporateEnrollmentId: number): number {
    return this.getCorporateParticipantsByEnrollment(corporateEnrollmentId).length;
  }

  // Métodos para manejar la moneda del proyecto
  getProjectCurrencyName(): string {
    if (this.projectData?.currency?.name) {
      return this.projectData.currency.name;
    }
    return 'Peso Colombiano'; // Por defecto
  }

  getProjectInscriptionAmount(): string {
    const currencyCode = this.projectData?.currency?.code || 'COP';
    const symbol = this.projectData?.currency?.symbol || '$';
    
    if (currencyCode === 'COP') {
      // Mostrar en COP
      const amount = this.projectData?.inscriptionAmountCOP || 0;
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } else if (currencyCode === 'USD') {
      // Mostrar en USD
      const amount = this.projectData?.inscriptionAmountUSD || 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } else {
      // Para otras monedas, usar el símbolo y el monto en USD como base
      const amount = this.projectData?.inscriptionAmountUSD || 0;
      return `${symbol}${new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)}`;
    }
  }

  shouldShowEquivalentAmount(): boolean {
    const currencyCode = this.projectData?.currency?.code || 'COP';
    return currencyCode !== 'COP' && this.projectData?.inscriptionAmountCOP > 0;
  }

  getEquivalentAmountCOP(): string {
    if (!this.shouldShowEquivalentAmount()) return '';
    
    const amount = this.projectData?.inscriptionAmountCOP || 0;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // 🆕 MÉTODOS PARA MANEJO DE MONEDA Y TRM EN COMPROBANTES DE INGRESO

  /**
   * Obtiene el nombre de la moneda de un comprobante
   */
  getReceiptCurrencyName(receipt: any): string {
    if (receipt?.currencyName) {
      return receipt.currencyName;
    }
    if (receipt?.currency?.name) {
      return receipt.currency.name;
    }
    return 'Peso Colombiano'; // Por defecto
  }

  /**
   * Obtiene el código de la moneda de un comprobante
   */
  getReceiptCurrencyCode(receipt: any): string {
    if (receipt?.currencyCode) {
      return receipt.currencyCode;
    }
    if (receipt?.currency?.code) {
      return receipt.currency.code;
    }
    return 'COP'; // Por defecto
  }

  /**
   * Obtiene el símbolo de la moneda de un comprobante
   */
  getReceiptCurrencySymbol(receipt: any): string {
    if (receipt?.currencySymbol) {
      return receipt.currencySymbol;
    }
    if (receipt?.currency?.symbol) {
      return receipt.currency.symbol;
    }
    return '$'; // Por defecto
  }

  /**
   * Verifica si un comprobante tiene TRM aplicada
   */
  hasExchangeRate(receipt: any): boolean {
    return receipt?.exchangeRate && receipt.exchangeRate > 0 && receipt.exchangeRate !== 1;
  }

  /**
   * Obtiene la TRM formateada de un comprobante
   */
  getFormattedExchangeRate(receipt: any): string {
    if (!this.hasExchangeRate(receipt)) return '';
    
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(receipt.exchangeRate);
  }

  /**
   * Obtiene información de conversión de moneda para mostrar en UI
   */
  getCurrencyConversionInfo(receipt: any): string {
    const currencyCode = this.getReceiptCurrencyCode(receipt);
    
    if (currencyCode === 'COP') {
      return 'Moneda nacional (COP)';
    }
    
    if (this.hasExchangeRate(receipt)) {
      const rate = this.getFormattedExchangeRate(receipt);
      return `Convertido con TRM: 1 ${currencyCode} = $${rate} COP`;
    }
    
    return `Moneda: ${currencyCode}`;
  }

  /**
   * Obtiene el monto original (antes de conversión) formateado
   */
  getOriginalAmount(receipt: any, field: 'gross' | 'net' | 'fees'): string {
    let originalAmount: number | null = null;
    
    switch (field) {
      case 'gross':
        originalAmount = receipt.originalGrossAmount;
        break;
      case 'net':
        originalAmount = receipt.originalNetAmount;
        break;
      case 'fees':
        originalAmount = receipt.originalTotalFees;
        break;
    }
    
    if (originalAmount === null || originalAmount === undefined) {
      return '';
    }
    
    const currencySymbol = this.getReceiptCurrencySymbol(receipt);
    const currencyCode = this.getReceiptCurrencyCode(receipt);
    
    return `${currencySymbol}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(originalAmount)} ${currencyCode}`;
  }

  /**
   * Verifica si debe mostrar el monto original
   */
  shouldShowOriginalAmount(receipt: any): boolean {
    return receipt.originalNetAmount !== null && receipt.originalNetAmount !== undefined;
  }

  /**
   * Verifica si debe mostrar información de conversión
   */
  shouldShowCurrencyConversion(receipt: any): boolean {
    const currencyCode = this.getReceiptCurrencyCode(receipt);
    return currencyCode !== 'COP' || this.hasExchangeRate(receipt);
  }

}
